import { Breadcrumbs } from 'components/bredcrambs';
import { useAuth } from 'context/auth';
import { Layout } from 'components/layout';
import { LoadMoreButton } from 'components/load-more-button';
import { CustomMetadata } from 'components/metadata';
import { ComplaintModal } from 'components/modal/complaint-modal';
import { LoginModal } from 'components/modal/login';
import { Title } from 'components/title';
import { allMetadata } from 'constants/metadata';
import { useState } from 'react';
import styles from './styles.module.css';

import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import { complaints as complaintsBreadcrumbs } from 'constants/routes-breadcrumbs';
import { TopicCommentSection as CommentSection } from 'components/template/topic-thread/comment-section';

import { useLoginModal } from 'hooks';
import { useComplaintsPagination } from 'hooks/useComplaintsPagination';

import { IComplaint } from 'constants/types';
import { ComplaintsDescription } from './description';
import { FAQComponent } from 'components/casino-faq';

interface Props {
  complaints: IComplaint[];
}

export const ComplaintsPage = ({ complaints }: Props) => {
  const [isModalVisible, setModalVisible] = useState<boolean>(false);

  const {
    page,
    isLast,
    setPage,
    isFetching,
    data: complaintsPagination = [],
  } = useComplaintsPagination({ collection: null });

  const { isLoggedIn } = useAuth();
  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();

  const data = complaintsPagination?.length
    ? complaintsPagination
    : (complaints ?? []);

  const showMoreButton = !isLast ? (
    <LoadMoreButton
      isAuto={false}
      isVisible={true}
      onClick={() => {
        setPage(page + 1);
      }}
      variant={'showMore'}
      description="Show more"
      isFetching={isFetching}
      className={styles.loadmoreButton}
    />
  ) : undefined;

  return (
    <div>
      <CustomMetadata metadata={allMetadata.allComplaints} />
      <Layout hasFooter isSearchInputMobileVisible bgVariant="basic">
        <main className={styles.main}>
          <div className={styles.container_s}>
            <div className={styles.breadcrumbs}>
              <Breadcrumbs items={[...complaintsBreadcrumbs]} />
            </div>
            <Title
              text="Complaints About Casinos – Help for Players"
              description="If the casino does not pay out your winnings, write a complaint, and we will try to help."
              button={
                <div className={styles.wrapper_btn}>
                  <Button
                    icon={<Icon.Veryfied />}
                    className={styles.button}
                    text={'New complaint'}
                    onClick={() =>
                      isLoggedIn
                        ? setModalVisible(true)
                        : handleVisibleLoginModal()
                    }
                  />
                </div>
              }
              gap={20}
            />
            <ComplaintModal
              isVisible={isModalVisible}
              setVisible={setModalVisible}
            />
            {data.length > 0 ? (
              <CommentSection
                variant="complaint"
                items={data}
                showMoreButton={showMoreButton}
              />
            ) : (
              !isFetching && (
                <div className={styles.emptyMessage}>
                  No complaints found. Be the first to submit a complaint!
                </div>
              )
            )}

            <ComplaintsDescription />

            <FAQComponent
              customFaq={[
                {
                  title: 'How long does it take to resolve a complaint?',
                  description:
                    'It depends on the complexity of the issue, but most cases are addressed within 2-4 weeks.',
                },
                {
                  title: 'Is this service free?',
                  description:
                    'Yes, our service is completely free for players.',
                },
                {
                  title: 'Can I complain about any casino?',
                  description:
                    'We focus on licensed and regulated casinos. If the casino is unlicensed, our influence may be limited.',
                },
                {
                  title: 'What happens if the casino refuses to cooperate?',
                  description:
                    'If the casino does not resolve the issue, it will be added to our public blacklist.',
                },
                {
                  title: 'Can I submit a complaint anonymously?',
                  description:
                    'No, we require your contact information to communicate with the casino on your behalf.',
                },
                {
                  title: 'What proof should I provide?',
                  description:
                    'Include all relevant evidence, such as emails, payment receipts, or account screenshots.',
                },
                {
                  title: 'What if my complaint is rejected?',
                  description:
                    'If your complaint violates our service rules, we will explain why and offer advice for alternative solutions.',
                },
              ]}
            />
          </div>
          <LoginModal
            isVisible={isLoginVisible}
            onClose={handleCloseLoginModal}
            confirmOnClick={handleConfirmLoginModal}
          />
        </main>
      </Layout>
    </div>
  );
};
